import { useMemo } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { UserButton } from "@clerk/clerk-react";

import { Wordmark } from "@/components/Brand";
import PageHead from "@/components/PageHead";

/**
 * Admin shell: persistent left rail on desktop, top tab bar on mobile.
 * Every /admin/* page carries noindex via PageHead. Design tokens from the
 * public pages are reused via samoony classes. No full-width pill buttons.
 */
const NAV = [
    { to: "/admin/creators", label: "Creators", testid: "admin-nav-creators" },
    { to: "/admin/work", label: "Work queue", testid: "admin-nav-work" },
    { to: "/admin/guides", label: "Guides", testid: "admin-nav-guides" },
    { to: "/admin/tracking", label: "Tracking", testid: "admin-nav-tracking" },
    { to: "/admin/tools", label: "Tools", testid: "admin-nav-tools" },
];

export default function AdminShell() {
    const location = useLocation();
    const title = useMemo(() => {
        const seg = location.pathname.split("/")[2] || "creators";
        const map = {
            creators: "Creators",
            work: "Work queue",
            guides: "Guides",
            tracking: "Tracking",
            tools: "Tools",
        };
        return `${map[seg] || "Admin"} | Samoony admin`;
    }, [location.pathname]);

    return (
        <div className="min-h-screen bg-paper">
            <PageHead title={title} noindex />
            {/* Top bar: logo + global actions (mobile shows nav below). */}
            <header
                data-testid="admin-topbar"
                className="sticky top-0 z-20 bg-paper/95 backdrop-blur border-b border-black/10"
            >
                <div className="flex items-center justify-between pl-4 pr-4 sm:pl-6 sm:pr-6 lg:pl-8 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <Link
                            to="/admin"
                            className="no-underline shrink-0"
                            data-testid="admin-logo-link"
                        >
                            <Wordmark height={22} />
                        </Link>
                        <span className="samoony-chip" data-testid="admin-topbar-badge">
                            Admin
                        </span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Link
                            to="/dashboard"
                            data-testid="admin-exit-link"
                            className="samoony-chip"
                        >
                            Exit admin
                        </Link>
                        <UserButton afterSignOutUrl="/" />
                    </div>
                </div>
                {/* Mobile nav */}
                <nav
                    aria-label="Admin sections (mobile)"
                    className="lg:hidden overflow-x-auto no-scrollbar border-t border-black/10"
                    data-testid="admin-nav-mobile"
                >
                    <ul className="flex items-center gap-1 px-3 py-2 min-w-max">
                        {NAV.map((item) => (
                            <li key={item.to}>
                                <NavLink
                                    to={item.to}
                                    data-testid={`${item.testid}-mobile`}
                                    className={({ isActive }) =>
                                        [
                                            "px-3 py-1.5 rounded-full text-sm whitespace-nowrap",
                                            isActive
                                                ? "bg-ink text-paper"
                                                : "text-black/70 hover:bg-black/5",
                                        ].join(" ")
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </nav>
            </header>

            {/* Body: left rail (desktop) + outlet */}
            <div className="flex items-start gap-6 px-4 sm:px-6 lg:px-8 py-6 max-w-[1400px] mx-auto">
                <aside
                    aria-label="Admin sections"
                    data-testid="admin-nav-desktop"
                    className="hidden lg:block w-56 shrink-0"
                >
                    <ul className="sticky top-24 flex flex-col gap-1">
                        {NAV.map((item) => (
                            <li key={item.to}>
                                <NavLink
                                    to={item.to}
                                    data-testid={item.testid}
                                    className={({ isActive }) =>
                                        [
                                            "block px-3 py-2 rounded-xl text-sm",
                                            isActive
                                                ? "bg-ink text-paper"
                                                : "text-black/80 hover:bg-black/5",
                                        ].join(" ")
                                    }
                                >
                                    {item.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </aside>
                <main
                    data-testid="admin-main"
                    className="flex-1 min-w-0 pb-24"
                >
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
